import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Search, Filter, CheckCircle, XCircle, Eye, Pause, Play } from 'lucide-react'

interface Project {
  id: string
  name: string
  country: string
  project_type: string
  verifier: string
  vintage_year: number
  status: 'pending' | 'approved' | 'rejected'
  provider_id: string
  created_at: string
  price_per_ton?: number
  total_supply?: number
  trading_active?: boolean
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([])
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null)

  useEffect(() => {
    fetchProjects()
  }, [])

  useEffect(() => {
    filterProjects()
  }, [projects, searchTerm, statusFilter])

  const fetchProjects = async () => {
    try {
      // Use admin RPC function to bypass RLS
      const { data, error } = await supabase
        .rpc('admin_get_all_projects')

      if (error) throw error
      setProjects(data || [])
    } catch (error) {
      console.error('Error fetching projects:', error)
    } finally {
      setLoading(false)
    }
  }

  const filterProjects = () => {
    let filtered = projects

    if (searchTerm) {
      filtered = filtered.filter(
        (project) =>
          project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          project.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
          project.verifier.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((project) => project.status === statusFilter)
    }

    setFilteredProjects(filtered)
  }

  const handleProjectAction = async (projectId: string, action: 'approve' | 'reject') => {
    try {
      const newStatus = action === 'approve' ? 'approved' : 'rejected'
      const { error } = await supabase
        .from('projects')
        .update({ status: newStatus })
        .eq('id', projectId)

      if (error) throw error

      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, status: newStatus } : p))
      )
      setShowModal(false)
      setSelectedProject(null)
      setActionType(null)
    } catch (error) {
      console.error('Error updating project:', error)
      alert('Failed to update project status')
    }
  }

  const toggleTrading = async (projectId: string, currentState: boolean) => {
    try {
      const { error } = await supabase
        .from('projects')
        .update({ trading_active: !currentState })
        .eq('id', projectId)

      if (error) throw error

      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, trading_active: !currentState } : p))
      )
    } catch (error) {
      console.error('Error toggling trading:', error)
      alert('Failed to toggle trading status')
    }
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'admin-badge-warning',
      approved: 'admin-badge-success',
      rejected: 'admin-badge-danger',
    }
    return badges[status as keyof typeof badges] || 'admin-badge'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-primary"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Project Management</h1>
        <p className="text-gray-600 mt-1">Review and manage carbon credit projects</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="admin-card">
          <p className="text-sm text-gray-600 mb-1">Total Projects</p>
          <p className="text-2xl font-bold">{projects.length}</p>
        </div>
        <div className="admin-card">
          <p className="text-sm text-gray-600 mb-1">Pending Review</p>
          <p className="text-2xl font-bold text-orange-600">
            {projects.filter((p) => p.status === 'pending').length}
          </p>
        </div>
        <div className="admin-card">
          <p className="text-sm text-gray-600 mb-1">Approved</p>
          <p className="text-2xl font-bold text-green-600">
            {projects.filter((p) => p.status === 'approved').length}
          </p>
        </div>
        <div className="admin-card">
          <p className="text-sm text-gray-600 mb-1">Rejected</p>
          <p className="text-2xl font-bold text-red-600">
            {projects.filter((p) => p.status === 'rejected').length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-admin-primary focus:border-admin-primary"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="text-gray-400 w-5 h-5" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-admin-primary focus:border-admin-primary"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projects Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Project Name</th>
                <th>Type</th>
                <th>Country</th>
                <th>Verifier</th>
                <th>Vintage</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    No projects found
                  </td>
                </tr>
              ) : (
                filteredProjects.map((project) => (
                  <tr key={project.id}>
                    <td className="font-medium">{project.name}</td>
                    <td>
                      <span className="capitalize">{project.project_type}</span>
                    </td>
                    <td>{project.country}</td>
                    <td>{project.verifier}</td>
                    <td>{project.vintage_year}</td>
                    <td>
                      <span className={getStatusBadge(project.status)}>
                        {project.status}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {project.status === 'pending' && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedProject(project)
                                setActionType('approve')
                                setShowModal(true)
                              }}
                              className="text-green-600 hover:text-green-700"
                              title="Approve"
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedProject(project)
                                setActionType('reject')
                                setShowModal(true)
                              }}
                              className="text-red-600 hover:text-red-700"
                              title="Reject"
                            >
                              <XCircle className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        {project.status === 'approved' && (
                          <button
                            onClick={() => toggleTrading(project.id, project.trading_active || false)}
                            className="text-admin-primary hover:text-admin-secondary"
                            title={project.trading_active ? 'Pause Trading' : 'Resume Trading'}
                          >
                            {project.trading_active ? (
                              <Pause className="w-5 h-5" />
                            ) : (
                              <Play className="w-5 h-5" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedProject(project)
                            setShowModal(true)
                          }}
                          className="text-gray-600 hover:text-gray-700"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Modal */}
      {showModal && selectedProject && actionType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">
              {actionType === 'approve' ? 'Approve Project' : 'Reject Project'}
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Are you sure you want to {actionType}{' '}
              <strong>{selectedProject.name}</strong>?
            </p>

            <div className="bg-gray-50 rounded-md p-4 mb-6 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="text-gray-600">Country:</div>
                <div className="font-medium">{selectedProject.country}</div>
                <div className="text-gray-600">Type:</div>
                <div className="font-medium capitalize">{selectedProject.project_type}</div>
                <div className="text-gray-600">Verifier:</div>
                <div className="font-medium">{selectedProject.verifier}</div>
                <div className="text-gray-600">Vintage:</div>
                <div className="font-medium">{selectedProject.vintage_year}</div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowModal(false)
                  setSelectedProject(null)
                  setActionType(null)
                }}
                className="flex-1 admin-button-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleProjectAction(selectedProject.id, actionType)}
                className={`flex-1 ${
                  actionType === 'approve'
                    ? 'admin-button-success'
                    : 'admin-button-danger'
                }`}
              >
                {actionType === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showModal && selectedProject && !actionType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Project Details</h3>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Project Name</label>
                <p className="font-medium">{selectedProject.name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600">Country</label>
                  <p className="font-medium">{selectedProject.country}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Project Type</label>
                  <p className="font-medium capitalize">{selectedProject.project_type}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Verifier</label>
                  <p className="font-medium">{selectedProject.verifier}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Vintage Year</label>
                  <p className="font-medium">{selectedProject.vintage_year}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Status</label>
                  <p>
                    <span className={getStatusBadge(selectedProject.status)}>
                      {selectedProject.status}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Created</label>
                  <p className="font-medium">
                    {new Date(selectedProject.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowModal(false)
                  setSelectedProject(null)
                }}
                className="admin-button-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
